import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { settingsAPI } from '../services/api'
import { useAuth } from './AuthContext'

const SettingsContext = createContext()
const DEFAULT_SETTINGS = {
  currency: 'INR',
  dateFormat: 'MM/DD/YYYY',
  timezone: 'UTC',
  currency_rates: { INR: 1, USD: 83 }
}

const getStoredToken = () => sessionStorage.getItem('token') || localStorage.getItem('token')

export const useSettings = () => {
  const context = useContext(SettingsContext)
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider')
  }
  return context
}

export const SettingsProvider = ({ children }) => {
  const { isAuthenticated, loading: authLoading } = useAuth()
  const [settings, setSettings] = useState(DEFAULT_SETTINGS)
  const [loading, setLoading] = useState(true)

  const fetchSettings = useCallback(async () => {
    if (!getStoredToken()) {
      setLoading(false)
      return null
    }

    try {
      const res = await settingsAPI.getAll()
      const data = res?.data?.data
      if (data) {
        // Handle potential legacy structure or flat structure
        let parsedSettings = { ...data }
        
        // Ensure currency is a string
        if (typeof parsedSettings.currency === 'object') {
           parsedSettings.currency = parsedSettings.currency.baseCurrency || 'USD'
        }

        // Ensure rates exist
        if (!parsedSettings.currency_rates && data.currency && typeof data.currency === 'object') {
           parsedSettings.currency_rates = data.currency.rates
        }

        setSettings(prev => ({
          ...prev,
          ...parsedSettings
        }))
      }
    } catch (error) {
      console.error('Failed to load settings:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (authLoading) return

    const token = getStoredToken()
    if (!isAuthenticated || !token) {
      setSettings(DEFAULT_SETTINGS)
      setLoading(false)
      return
    }

    setLoading(true)
    fetchSettings()
  }, [authLoading, fetchSettings, isAuthenticated])

  const updateSettings = useCallback(async (newSettings) => {
    try {
      // Optimistic update
      setSettings(prev => ({ ...prev, ...newSettings }))
      
      // Persist
      await settingsAPI.updateAll(newSettings)
      
      // Refetch to ensure sync
      await fetchSettings()
      return true
    } catch (error) {
      console.error('Failed to update settings:', error)
      // Revert on failure (could implement more robust rollback)
      await fetchSettings() 
      return false
    }
  }, [fetchSettings])

  // Helper: Format Date based on global settings
  const formatDate = useCallback((dateString, formatStr) => {
    if (!dateString) return '-'
    try {
      const date = new Date(dateString)
      if (isNaN(date.getTime())) return dateString

      const tz = settings.timezone || 'UTC'
      const fmt = formatStr || settings.dateFormat || 'MM/DD/YYYY'

      const getYMD = () => {
        const parts = new Intl.DateTimeFormat('en-US', {
          timeZone: tz,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        }).formatToParts(date)
        const map = Object.fromEntries(parts.map(p => [p.type, p.value]))
        const yyyy = map.year
        const mm = map.month
        const dd = map.day
        return { yyyy, mm, dd }
      }

      if (fmt === 'MM/DD/YYYY') {
        const { mm, dd, yyyy } = getYMD()
        return `${mm}/${dd}/${yyyy}`
      }
      if (fmt === 'DD/MM/YYYY') {
        const { mm, dd, yyyy } = getYMD()
        return `${dd}/${mm}/${yyyy}`
      }
      if (fmt === 'YYYY-MM-DD') {
        const { mm, dd, yyyy } = getYMD()
        return `${yyyy}-${mm}-${dd}`
      }

      // Fallback: try to infer common patterns with month name
      if (fmt.includes('MMM')) {
        return new Intl.DateTimeFormat('en-US', {
          timeZone: tz,
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        }).format(date)
      }

      // Default fallback to MM/DD/YYYY
      const { mm, dd, yyyy } = getYMD()
      return `${mm}/${dd}/${yyyy}`
    } catch (e) {
      console.error('Date formatting error:', e)
      return String(dateString)
    }
  }, [settings.dateFormat, settings.timezone])

  const value = {
    settings,
    loading,
    updateSettings,
    fetchSettings,
    formatDate,
    // Expose individual common settings for ease of use
    currency: settings.currency,
    timezone: settings.timezone,
    dateFormat: settings.dateFormat,
    currencyRates: settings.currency_rates
  }

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  )
}
